import { memo, useId, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import InsightsSectionHeading from "./InsightsSectionHeading";
import { LIFT_BALANCE_LENS_IDS } from "../utils/insightsLiftBalance";

/**
 * Lift Balance: contested-lift set distribution with colored badges + databars.
 */
function InsightsLiftBalanceSection({ liftBalance, lensId }) {
  const { t } = useTranslation("athlete-portal");
  const p = "programs.programInsights.panel";
  const titleId = useId();

  const tooltipBody = useMemo(() => {
    const targetKey = `${p}.liftBalanceTypicalTarget.${lensId}`;
    const targetLine = t(targetKey, { defaultValue: "" });
    return [
      t(`${p}.liftBalanceInfoIntro`),
      "",
      t(`${p}.liftBalanceTypicalTargetLabel`),
      targetLine,
      "",
      t(`${p}.liftBalanceInfoImbalances`),
    ]
      .join("\n")
      .replace(/\n{3,}/g, "\n\n");
  }, [lensId, t]);

  if (!liftBalance?.rows?.length) {
    return null;
  }

  return (
    <section
      className="ProgramInsights-liftBalance"
      aria-labelledby={titleId}
    >
      <InsightsSectionHeading
        titleId={titleId}
        title={t(`${p}.liftBalanceLabel`)}
        infoAriaLabel={t(`${p}.liftBalanceLabel`)}
        infoTooltipBody={tooltipBody}
        showRule={false}
      />
      <ul className="ProgramInsights-liftBalance-list">
        {liftBalance.rows.map((row) => (
          <li
            key={row.rowId}
            className={`ProgramInsights-liftBalance-row ProgramInsights-liftBalance-row--${row.badgeVariant}`}
          >
            <span
              className={`ProgramInsights-liftBalance-badge ProgramInsights-liftBalance-badge--${row.badgeVariant}`}
              aria-hidden="true"
            >
              {row.badgeLetter}
            </span>
            <span className="ProgramInsights-liftBalance-label">
              {t(`${p}.lifts.${row.nameKey}.name`)}
            </span>
            <div className="ProgramInsights-liftBalance-track" aria-hidden="true">
              <div
                className={`ProgramInsights-liftBalance-fill ProgramInsights-liftBalance-fill--${row.badgeVariant}`}
                style={{ width: `${row.pct}%` }}
              />
            </div>
            <span className="ProgramInsights-liftBalance-pct">
              {row.pctDisplay}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

InsightsLiftBalanceSection.propTypes = {
  lensId: PropTypes.oneOf([...LIFT_BALANCE_LENS_IDS]).isRequired,
  liftBalance: PropTypes.shape({
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        rowId: PropTypes.string.isRequired,
        nameKey: PropTypes.string.isRequired,
        badgeLetter: PropTypes.string.isRequired,
        badgeVariant: PropTypes.string.isRequired,
        pct: PropTypes.number.isRequired,
        pctDisplay: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
};

export default memo(InsightsLiftBalanceSection);
